import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { DateTime } from "luxon"
import { useField, useFormikContext } from "formik"
import { Box, Divider, FormHelperText, Grid, InputAdornment, Switch, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons"
import { FormikSwitch, FormikTextField } from "../../../../../shared/components/forms/Formik"
import { sanitizeAndLimitInput } from "../../../../../shared/components/createOrder/components/form/helpers"
import { INVALID_INPUT_KEY } from "../../../../nonAdminUser/reports/settings/wallets/refill/components/WalletsRefillManual"
import { FormikImageUpload } from "../../../../../shared/components/Formik/FormikImageUpload"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { AvailabilityDetailsField } from "./AvailabilityDetailsField"
import { FormikProductGroupField } from "./FormikProductGroupField"
import { CreateUpdateProductFormValues, OfferDetailsType } from "../productsTypes"
import { FormikAllergensField } from "./FormikAllergensField"
import { DeadlineField, dayOptions } from "./DeadlineField"
import { FormikCurrencyField } from "./FormikCurrencyField"
import { OfferDetails } from "./offerDetails/OfferDetails"
// import { ItemLimitsField } from "./ItemLimitsField"
import { FormikPriceListField } from "./FormikPriceListsField"
import { StockField } from "./stockManagement/StockField"
import { InfoBox } from "../../shops/components/formElements/InfoBox"

const useStyles = makeStyles()((theme) => ({
    priceDisabledIcon: {
        color: theme.palette.tailwind.amber[500],
    },
    hidePriceSwitch: {
        "& .MuiFormControl-root": {
            width: "10rem",
        },
    },
    generalRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    onSaleInput: {
        width: "5rem",
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
        "& .MuiInputBase-input": {
            textAlign: "right",
        },
    },
}))

type CreateUpdateProductFormContentProps = Readonly<{
    isEditMode?: boolean
}>

function OnSalePerDayField() {
    const { classes } = useStyles()
    const { submitCount } = useFormikContext()
    const [, meta] = useField("itemsForSalePerDay")
    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    return (
        <Box>
            <Box className={classes.generalRow}>
                <LocalizedStrict id="product-stock-on-sale-per-day-label">
                    <Typography variant="body1">On sale per day:</Typography>
                </LocalizedStrict>
                <FormikTextField
                    name="itemsForSalePerDay"
                    type="number"
                    className={classes.onSaleInput}
                    size="small"
                    FormHelperTextProps={{ style: { display: "none" } }}
                    inputProps={{
                        min: 0,
                        inputMode: "numeric" as const,
                        onInput: (e: React.FormEvent<HTMLInputElement>) => {
                            const sanitized = sanitizeAndLimitInput(e.currentTarget.value, 6)
                            if (e.currentTarget.value !== sanitized) {
                                e.currentTarget.value = sanitized
                            }
                        },
                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (INVALID_INPUT_KEY.includes(e.key)) {
                                e.preventDefault()
                            }
                        },
                    }}
                />
            </Box>
            {showError && (
                <FormHelperText error sx={{ mt: 0 }}>
                    {meta.error}
                </FormHelperText>
            )}
        </Box>
    )
}

export function CreateUpdateProductFormContent({ isEditMode }: CreateUpdateProductFormContentProps) {
    const { classes } = useStyles()
    const { values, setFieldValue, errors } = useFormikContext<CreateUpdateProductFormValues>()
    const defaultTime = DateTime.fromISO("12:00")
    const priceListId = values.priceList?.id
    const isFirstRender = useRef(true)
    const [searchParams] = useSearchParams()

    const [checkedAvailability, setCheckedAvailability] = useState(
        !!(values?.availabilityDetails?.dateInterval?.from || values?.availabilityDetails?.dateInterval?.to),
    )
    const [checkedDeadline, setCheckedDeadline] = useState(!!values?.deadline)
    const [checkedItemLimits, setCheckedItemLimits] = useState(!!values?.itemLimits)
    const [checkedOffer, setCheckedOffer] = useState(!!values?.offerDetails)
    const [checkedStock, setCheckedStock] = useState(values?.stock != null)
    const [offerDialogState, setOfferDialogState] = useState<boolean>(
        searchParams.get("openOffer") === "true" && !!values?.offerDetails,
    )
    const [cachedAvailabilityDateInterval, setCachedAvailabilityDateInterval] = useState(
        values?.availabilityDetails?.dateInterval,
    )
    const [cachedDeadline, setCachedDeadline] = useState(values?.deadline)
    const [cachedItemLimits, setCachedItemLimits] = useState(values?.itemLimits)
    const [cachedOfferDetails, setCachedOfferDetails] = useState(values?.offerDetails)
    const [cachedStock, setCachedStock] = useState(values?.stock)
    const handleChangeAvailability = (checked: boolean) => {
        setCheckedAvailability(checked)
        if (checked) {
            const restored = cachedAvailabilityDateInterval ?? { from: null, to: null }
            setFieldValue("availabilityDetails.dateInterval", restored)
        } else {
            setCachedAvailabilityDateInterval(values?.availabilityDetails?.dateInterval)
            setFieldValue("availabilityDetails.dateInterval", undefined)
        }
    }
    const handleChangeDeadline = (checked: boolean) => {
        setCheckedDeadline(checked)
        if (checked) {
            const restored = cachedDeadline ?? {
                purchases: { daysBefore: dayOptions[0].value, timeOfDay: defaultTime },
                // hide cancellation for now
                // cancellation: { daysBefore: dayOptions[0].value, timeOfDay: defaultTime },
            }
            setFieldValue("deadline", restored)
        } else {
            setCachedDeadline(values?.deadline)
            setFieldValue("deadline", null)
        }
    }
    const handleChangeItemLimits = (checked: boolean) => {
        setCheckedItemLimits(checked)
        if (checked) {
            const restored = cachedItemLimits ?? {
                maxDailyItemsPerPerson: "",
                minItemsPerOrder: "",
            }
            setFieldValue("itemLimits", restored)
        } else {
            setCachedItemLimits(values?.itemLimits)
            setFieldValue("itemLimits", null)
        }
    }
    const offerSavedRef = useRef(!!values?.offerDetails)
    const handleChangeOffer = (checked: boolean) => {
        setCheckedOffer(checked)
        if (checked) {
            if (cachedOfferDetails) {
                setFieldValue("offerDetails", cachedOfferDetails)
            } else {
                offerSavedRef.current = false
                setOfferDialogState(true)
            }
        } else {
            setCachedOfferDetails(values?.offerDetails)
            setFieldValue("offerDetails", undefined)
        }
    }
    const handleOfferSave = (data: OfferDetailsType) => {
        offerSavedRef.current = true
        setFieldValue("offerDetails", data)
        setCheckedOffer(true)
    }
    const handleOfferDialogClose = () => {
        setOfferDialogState(false)
        if (!offerSavedRef.current) {
            setCheckedOffer(false)
        }
    }
    const handleChangeStock = (checked: boolean) => {
        setCheckedStock(checked)
        if (checked) {
            const restored = cachedStock ?? {
                total: undefined,
                inventoryIncomplete: false,
            }
            setFieldValue("stock", restored)
        } else {
            setCachedStock(values?.stock)
            setFieldValue("stock", null)
        }
    }

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        setFieldValue("productGroup", null)
    }, [priceListId])

    useEffect(() => {
        if (values.variablePrice) {
            setFieldValue("price", "")
            setFieldValue("isPriceHidden", false)
        }
    }, [values.variablePrice])

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    {/* <FormikPriceListsField name="priceLists" /> */}
                    <FormikPriceListField name="priceList" />

                    <FormikProductGroupField name="productGroup" priceListId={priceListId} />

                    <LocalizedStrict id="product-create-name-field-label" attrs={{ label: true }}>
                        <FormikTextField name="name" label="Product name" fullWidth />
                    </LocalizedStrict>

                    <Grid container item spacing={1} direction="row" alignItems="flex-start">
                        <Grid item sm={5}>
                            <LocalizedStrict id="product-create-price-field-label" attrs={{ label: true }}>
                                <FormikCurrencyField
                                    name="price"
                                    label="Price"
                                    errorMessage={errors.price}
                                    disabled={values.variablePrice}
                                    startAdornment={<InputAdornment position="start">{values.currency}</InputAdornment>}
                                />
                            </LocalizedStrict>
                        </Grid>
                        {values.variablePrice && (
                            <Grid item display="flex" alignItems="center" height="4rem">
                                <LocalizedStrict id="product-create-price-disabled-tooltip" attrs={{ title: true }}>
                                    <Tooltip
                                        arrow
                                        placement="top"
                                        title="Price is disabled because variable price is active"
                                    >
                                        <FontAwesomeIcon
                                            icon={faCircleExclamation}
                                            size="lg"
                                            className={classes.priceDisabledIcon}
                                        />
                                    </Tooltip>
                                </LocalizedStrict>
                            </Grid>
                        )}
                        <Grid
                            item
                            sm
                            flexGrow={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            height="4rem"
                            className={classes.hidePriceSwitch}
                        >
                            <LocalizedStrict id="product-create-payment-price-switch">
                                <FormikSwitch<CreateUpdateProductFormValues>
                                    name="isPriceHidden"
                                    disabled={values.variablePrice}
                                >
                                    Hide price:
                                </FormikSwitch>
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                </StandardMainPanel>
                <LocalizedStrict id="product-create-panel-description" attrs={{ title: true }}>
                    <StandardMainPanel title="Description">
                        <LocalizedStrict
                            id="product-create-product-description-marketing-tag-field"
                            attrs={{ label: true, helperText: true }}
                        >
                            <FormikTextField
                                name="productDescription.marketingTag"
                                label="Marketing tag"
                                helperText="Examples: 'Offer', 'CO2', 'Hot dish', 'Vegetarian'"
                                fullWidth
                            />
                        </LocalizedStrict>
                        <FormikAllergensField name="productDescription.allergens" />
                        <LocalizedStrict id="product-create-description-field-label" attrs={{ label: true }}>
                            <FormikTextField name="description" label="Description" rows={4} fullWidth multiline />
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-image-field-label" attrs={{ label: true }}>
                            <FormikImageUpload label="Image" name="productPhoto" typeOfEntity="product" />
                        </LocalizedStrict>
                    </StandardMainPanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-general-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="General" variant="collapsible">
                        <LocalizedStrict id="product-create-active-switch">
                            <FormikSwitch name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-admin-only-switch">
                            <FormikSwitch<CreateUpdateProductFormValues> name="isAdminOnly">Staff only</FormikSwitch>
                        </LocalizedStrict>
                        <Divider />
                        <OnSalePerDayField />
                        <LocalizedStrict id="product-stock-on-sale-per-day-info" attrs={{ description: true }}>
                            <InfoBox description="Using this you can limit the number of items on sale each day." />
                        </LocalizedStrict>
                        <Divider />
                        <Box className={classes.generalRow}>
                            <LocalizedStrict id="product-create-stock-switch-label">
                                <Typography variant="body1">Stock management:</Typography>
                            </LocalizedStrict>
                            <Switch checked={checkedStock} onChange={(_, checked) => handleChangeStock(checked)} />
                        </Box>
                        {checkedStock && <StockField />}
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-pos-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="POS" variant="collapsible">
                        <LocalizedStrict id="product-create-variable-price-switch">
                            <FormikSwitch<CreateUpdateProductFormValues> name="variablePrice">
                                Activate variable price
                            </FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-require-staff-pin-switch">
                            <FormikSwitch<CreateUpdateProductFormValues> name="isLocked">
                                Require staff PIN
                            </FormikSwitch>
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-offer-panel" attrs={{ title: true, offContent: true }}>
                    <StandardSidePanel
                        title="Offer"
                        variant="collapsibleWithToggle"
                        checked={checkedOffer}
                        onCheckChange={handleChangeOffer}
                        offContent="Offer not activated"
                    >
                        <OfferDetails
                            dialogState={offerDialogState}
                            setDialogState={setOfferDialogState}
                            setCheckedOffer={setCheckedOffer}
                            onDialogClose={handleOfferDialogClose}
                            onSaveOffer={handleOfferSave}
                        />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-availability-panel" attrs={{ title: true, offContent: true }}>
                    <StandardSidePanel
                        title="Availability"
                        variant="collapsibleWithToggle"
                        checked={checkedAvailability}
                        onCheckChange={handleChangeAvailability}
                        offContent="Availability not activated"
                    >
                        <AvailabilityDetailsField />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-deadline-panel" attrs={{ title: true, offContent: true }}>
                    <StandardSidePanel
                        title="Deadline"
                        variant="collapsibleWithToggle"
                        checked={checkedDeadline}
                        onCheckChange={handleChangeDeadline}
                        offContent="Deadline not activated"
                    >
                        <DeadlineField />
                    </StandardSidePanel>
                </LocalizedStrict>

                {/* hide for now as we don't handle it in mobile and pos */}
                {/* <LocalizedStrict id="product-create-order-limits-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Order limits"
                        variant="collapsibleWithToggle"
                        checked={checkedItemLimits}
                        onCheckChange={handleChangeItemLimits}
                        offContent="Order limits not activated"
                    >
                        <ItemLimitsField />
                    </StandardSidePanel>
                </LocalizedStrict> */}
            </StandardForm>
        </>
    )
}
